import {Button} from "@zendeskgarden/react-buttons";
import {Label} from "@zendeskgarden/react-forms";
import {ReactElement, useContext, useEffect, useMemo, useRef, useState} from "react";
import {toast} from "react-toastify";
import {
    BulkAddDriversMutationVariables,
    DistributionMethod,
    DriverType,
    GrowthCalcMethod,
    MonthlyAdjustmentType,
    MonthlyAverageBasePeriod,
    OperationalMetricType,
    PayrollPrimaryCompensationItem,
    PercentGrowthBasePeriod,
    useBulkAddDriversMutation,
    VersionType
} from "../../../__generated__/generated_types";
import {CustomZDDropdown} from "../../../atoms/custom-zd-dropdown/CustomZDDropdown";
import {
    IDriverRow,
    IDriverRowAccountPercentage,
    IDriverRowCustom,
    IDriverRowLineItem,
    IDriverRowOperational, IDriverRowOperationalMonthlyAverage
} from "../../../components/account-table/logic/DriverHelpers";
import {
    NonSavingAccountTable,
    TNonSavingAccountTableCombinedData
} from "../../../components/account-table/NonSavingAccountTable";
import {YearMonthRangeForProperty} from "../../../components/year-month-range/YearMonthRangeForProperty";
import {TDriverMetrics, TDriverPayrollMetric} from "../../../contexts/account/data/logic/driversAndWorksheetData";
import {
    buildDummyData,
    IDriversAndWorksheetData,
    ISaveData
} from "../../../contexts/account/data/useDriversAndWorksheetData";
import {AuthContext} from "../../../contexts/AuthContext";
import {Property, useProperties} from "../../../contexts/properties/PropertiesContext";
import * as css from "../../../pages/workflows/account/styles/styles.module.scss";
import {getMonths} from "../../../utils/months-helper";
import FormulaBar, {FORMULA_BAR_DUMMY_ACCOUNT_ID} from "../../workflows/account/formula-bar/FormulaBar";
import {useFormulaBarAccountTableMapper} from "../logic/useFormulaBarAccountTableMapper";
import {SelectableFinancialEntity} from "../PropertyDrivers";
import * as bulkCss from "./styles/bulk-add-drivers.module.scss";

// eslint-disable-function @typescript-eslint/no-non-null-assertion
function buildCombinedData(
    dwdData: IDriversAndWorksheetData,
    driverAssumptions: Map<string, IDriverRow>,
    opMetricTypes: Pick<OperationalMetricType, "id" | "name">[]
): TNonSavingAccountTableCombinedData {
    /* eslint-disable @typescript-eslint/no-non-null-assertion */
    return{
        driversAndWorksheetData: {
            accountPercentageDriver: {
                isDriven: dwdData.parsedData!.drivers.account.length > 0,
                sourceAccounts: dwdData.parsedData!.drivers.account.map(a => {
                    // The assumptions table uses the source account id. This isn't so problematic here;
                    // it's just icky.
                    const assumptions = driverAssumptions.get(a.accountId);
                    let assumptionsAndValues;
                    if(assumptions && assumptions.driverType === DriverType.AccPercentage) {
                        const accAssumptions = assumptions as IDriverRowAccountPercentage;
                        assumptionsAndValues = accAssumptions.percentage.map(v => ({
                            percentage: v,
                            value: null,
                            lookbackPeriod: a.lookbackPeriod
                        }));
                    } else {
                        assumptionsAndValues = new Array(12).fill({
                            percentage: null,
                            value: null,
                            lookbackPeriod: a.lookbackPeriod
                        });
                    }

                    return {
                        ...a,
                        assumptionId: a.acctPercentageDriverAccountId,
                        assumptionsAndValues: assumptionsAndValues
                    };
                }),
                augments: {
                    maxValue: null,
                    minValue: null
                }
            },
            customDriver: {
                isDriven: dwdData.parsedData!.drivers.customDriver.length > 0,
                items: dwdData.parsedData!.drivers.customDriver.map(c => {
                    const assumptions = driverAssumptions.get(c.id);
                    let assumptionsAndValues;
                    if(assumptions) {
                        const customAssumptions = assumptions as IDriverRowCustom;
                        assumptionsAndValues = customAssumptions.amount.map((a, idx) => ({
                            amount: a,
                            count: customAssumptions.count[idx],
                            percentOf: customAssumptions.percentage[idx]
                        }));
                    } else {
                        assumptionsAndValues = new Array(12).fill({
                            amount: null,
                            count: null,
                            percentOf: null
                        });
                    }
                    return {
                        id: c.id,
                        itemName: c.itemName,
                        assumptions: assumptionsAndValues,
                        locked: false,
                        valuesLocked: false,
                    };
                })
            },
            growthDriver: dwdData.parsedData!.drivers.annualTargetValue
                .concat(dwdData.parsedData!.drivers.percentGrowth)
                .concat(dwdData.parsedData!.drivers.monthlyAverage)
                .concat(dwdData.parsedData!.drivers.yearlyAverage)
                .map(a => {
                    return {
                        ...a,
                        year: dwdData.parsedData!.year,
                        versionType: dwdData.parsedData!.versionType,
                        propertyId: "",
                        accountId: "",
                        annualValues: new Array(12).fill(null)
                    };
                }),
            operationalMetricDriver: {
                isDriven: dwdData.parsedData!.drivers.operational.length > 0,
                sourceMetrics: dwdData.parsedData!.drivers.operational.map(o => {
                    const assumptions = driverAssumptions.get(o.driverMetricTypeId);
                    let assumptionsAndValues;
                    if(assumptions && assumptions.driverType === DriverType.Operational) {
                        const opAssumptions = assumptions as IDriverRowOperational;
                        assumptionsAndValues = opAssumptions.percentage.map((v, idx) => ({
                            percentage: v,
                            amount: opAssumptions.fee[idx],
                            metricValue: null,
                            lookbackPeriod: o.lookbackPeriod
                        }));
                    } else {
                        assumptionsAndValues = new Array(12).fill({
                            percentage: null,
                            amount: null,
                            metricValue: null,
                            lookbackPeriod: o.lookbackPeriod
                        });
                    }

                    return {
                        ...o,
                        metricName: opMetricTypes.find(t => t.id === o.sourceMetricId)?.name ?? "UNKNOWN",
                        secondaryMetricName: opMetricTypes.find(t => t.id === o.secondaryMetricId)?.name ?? undefined,
                        assumptionsAndValues: assumptionsAndValues
                    };
                })
            },
            payrollDriver: {
                isDriven: dwdData.parsedData!.drivers.payroll.length > 0,
                compensationItems: dwdData.parsedData!.drivers.payroll.map(p => {
                    return {
                        itemType: p.itemType,
                        positions: p.positions
                    };
                })
            },
            renovationsDriver: {
                isDriven: false,
                costCategories: []
            },
            revenueDriver: {
                isDriven: false,
                sourceRevenueMetrics: []
            },
            worksheetDriver: {
                isDriven: dwdData.parsedData!.drivers.worksheet.length > 0,
                worksheetLines: dwdData.parsedData!.drivers.worksheet.map(w => {
                    const assumptions = driverAssumptions.get(w.description);
                    let values;
                    if(assumptions) {
                        const lineItem = assumptions as IDriverRowLineItem;
                        values = lineItem.values;
                    } else {
                        values = new Array(12).fill(null);
                    }
                    return {
                        lineId: "lineId",
                        values: values,
                        description: w.description,
                        locked: false, // TODO: this will be set by user when enabled in the UI. Until then we set it to false to fullfill the type. Has no effect on the backend.
                        valuesLocked: false,
                    };
                })
            },
            operationalMonthlyAverage: !dwdData.parsedData!.drivers.operationalMonthlyAverage ?
                null : {
                    isDriven: true,
                    locked: false,
                    assumptionsAndValues: {
                        operationalMetricType: dwdData.parsedData!.drivers.operationalMonthlyAverage.sourceMetric,
                        startMonth: dwdData.parsedData!.drivers.operationalMonthlyAverage.startMonth,
                        endMonth: dwdData.parsedData!.drivers.operationalMonthlyAverage.endMonth,
                        monthlyGrowth: dwdData.parsedData!.drivers.operationalMonthlyAverage.monthlyGrowth,
                        yearlyGrowth: dwdData.parsedData!.drivers.operationalMonthlyAverage.yearlyGrowth,
                        customIncrease: dwdData.parsedData!.drivers.operationalMonthlyAverage.customIncrease ?? false,
                        customIncreases: (driverAssumptions.get("op_monthly_avg") as IDriverRowOperationalMonthlyAverage | undefined)?.customIncreases ?? new Array(12).fill(null),
                        operationalMetricValues: new Array(12).fill(null),
                        amounts: new Array(12).fill(null)
                    }
                }
        },
        financialYearValues: {
            years: {}
        }
    };
    /* eslint-enable @typescript-eslint/no-non-null-assertion */
}

function valuesOrZeros(value: (string | null)[] | undefined): string[] {
    if(value) {
        return value.map(v => v !== undefined && v !== null && v !== "" ? v : "0");
    }
    return new Array(12).fill("0");
}

function mapForSave(
        propertyIds: string[],
        accountIds: string[],
        versionType: VersionType.Budget | VersionType.Reforecast,
        year: number,
        dwd: TDriverMetrics,
        assumptionsMap: Map<string, IDriverRow>,
        payrollCompItemsMap: Map<string, string>
): BulkAddDriversMutationVariables {
    const partialVars: Partial<BulkAddDriversMutationVariables> = {};
    if(dwd.drivers.account.length > 0) {
        partialVars["accountPercentage"] = dwd.drivers.account.map(a => {
            const assumptions = assumptionsMap.get(a.accountId) as IDriverRowAccountPercentage | undefined;
            return {
                delayRecognition: a.lookbackPeriod ?? 0,
                monthlyPercents: valuesOrZeros(assumptions?.percentage),
                sourceAccountId: a.accountId,
                minValue: null,
                maxValue: null
            };
        });
    }
    if(dwd.drivers.customDriver.length > 0) {
        partialVars["custom"] = dwd.drivers.customDriver.map(c => {
            const assumptions = assumptionsMap.get(c.id) as IDriverRowCustom | undefined;
            return {
                itemName: c.itemName,
                monthlyPercents: valuesOrZeros(assumptions?.percentage),
                monthlyAmounts: valuesOrZeros(assumptions?.amount),
                monthlyCount: valuesOrZeros(assumptions?.count)
            };
        });
    }
    if(dwd.drivers.operational.length > 0) {
        partialVars["operational"] = dwd.drivers.operational.map(o => {
            const assumptions = assumptionsMap.get(o.driverMetricTypeId) as IDriverRowOperational | undefined;
            return {
                name: o.name,
                delayRecognition: o.lookbackPeriod ?? 0,
                operationalMetricTypeId: o.sourceMetricId,
                secondaryOperationalMetricTypeId: o.secondaryMetricId ?? null,
                monthlyAmounts: valuesOrZeros(assumptions?.fee),
                monthlyPercents: valuesOrZeros(assumptions?.percentage),
                cumSum: o.cumSum,
                nonProrated: o.nonProrated,
                minValue: o.minValue,
                maxValue: o.maxValue,
            };
        });
    }
    if(dwd.drivers.worksheet.length > 0) {
        partialVars["lineItems"] = dwd.drivers.worksheet.map(w => {
            const assumptions = assumptionsMap.get(w.description) as IDriverRowLineItem | undefined;
            return {
                description: w.description,
                monthlyValues: valuesOrZeros(assumptions?.values)
            };
        });
    }
    if(dwd.drivers.payroll.length > 0) {
        const isPrimaryPayrollItem = (itemType: string) => itemType === PayrollPrimaryCompensationItem.Compensation ||
                itemType === PayrollPrimaryCompensationItem.Bonus ||
                itemType === PayrollPrimaryCompensationItem.Overtime;
        const primaryPayroll: TDriverPayrollMetric[] = [];
        const compItemsPayroll: TDriverPayrollMetric[] = [];
        for(const pItem of dwd.drivers.payroll) {
            if(isPrimaryPayrollItem(pItem.itemType)) {
                primaryPayroll.push(pItem);
            } else {
                compItemsPayroll.push(pItem);
            }
        }

        if(primaryPayroll.length > 0) {
            partialVars["payrollCompensation"] = primaryPayroll.flatMap(p => {
                return p.positions.map(pos => ({
                    primaryCompensationItem: p.itemType as PayrollPrimaryCompensationItem,
                    positionId: pos.id
                }));
            });
        }
        if(compItemsPayroll.length > 0) {
            partialVars["payrollCompensationItem"] = compItemsPayroll.flatMap(p => {
                const compItemId = payrollCompItemsMap.get(p.itemType);
                if(!compItemId) {
                    return [];
                }
                return p.positions.map(pos => ({
                    compensationItemId: compItemId,
                    positionId: pos.id
                }));
            });
        }
    }
    if(dwd.drivers.monthlyAverage.length > 0) {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const monthlyAverage = dwd.drivers.monthlyAverage[0]!;
        partialVars["growth"] = {
            growthCalcMethod: GrowthCalcMethod.MonthlyAverage,
            monthlyAverage: {
                monthlyAdjustmentValue: monthlyAverage.monthlyAdjustmentValue?.toString() ?? "0",
                monthlyAdjustmentType: monthlyAverage.monthlyAdjustmentType ?? MonthlyAdjustmentType.Dollar,
                monthlyAverageBasePeriod: monthlyAverage.monthlyAverageBasePeriod ?? MonthlyAverageBasePeriod.LastThreeMonthsActuals,
                lookbackPeriodStart: monthlyAverage.lookbackPeriodStart,
                lookbackPeriodEnd: monthlyAverage.lookbackPeriodEnd
            }
        };
    }
    if(dwd.drivers.yearlyAverage.length > 0) {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const yearlyAverage = dwd.drivers.yearlyAverage[0]!;
        partialVars["growth"] = {
            growthCalcMethod: GrowthCalcMethod.YearlyAverage,
            yearlyAverage: {
                monthlyAdjustmentValue: yearlyAverage.monthlyAdjustmentValue?.toString() ?? "0",
                monthlyAdjustmentType: yearlyAverage.monthlyAdjustmentType ?? MonthlyAdjustmentType.Dollar,
                monthlyAverageBasePeriod: yearlyAverage.monthlyAverageBasePeriod ?? MonthlyAverageBasePeriod.LastThreeMonthsActuals,
                lookbackPeriodStart: yearlyAverage.lookbackPeriodStart,
                lookbackPeriodEnd: yearlyAverage.lookbackPeriodEnd
            }
        };
    }
    if(dwd.drivers.percentGrowth.length > 0) {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const percentGrowth = dwd.drivers.percentGrowth[0]!;
        partialVars["growth"] = {
            growthCalcMethod: GrowthCalcMethod.PercentGrowth,
            percentGrowth: {
                percentGrowthAdjustmentValue: percentGrowth.percentGrowthAdjustmentValue?.toString() ?? "0",
                percentGrowthBasePeriod: percentGrowth.percentGrowthBasePeriod ?? PercentGrowthBasePeriod.SameMonthLastYear,
                distributionMethod: percentGrowth.distributionMethod ?? DistributionMethod.Flat
            }
        };
    }
    if(dwd.drivers.annualTargetValue.length > 0) {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const annualTargetValue = dwd.drivers.annualTargetValue[0]!;
        partialVars["growth"] = {
            growthCalcMethod: GrowthCalcMethod.AnnualTargetValue,
            annualTargetValue: {
                annualTargetValue: annualTargetValue.annualTargetValueManualEntry?.toString() ?? "0",
                distributionMethod: annualTargetValue.distributionMethod ?? DistributionMethod.Flat
            }
        };
    }
    if(dwd.drivers.operationalMonthlyAverage) {
        const assumptions = assumptionsMap.get("op_monthly_avg") as IDriverRowOperationalMonthlyAverage | undefined;
        partialVars["operationalMonthlyAverage"] = {
            operationalMetric: dwd.drivers.operationalMonthlyAverage.sourceMetric,
            startMonth: dwd.drivers.operationalMonthlyAverage.startMonth,
            endMonth: dwd.drivers.operationalMonthlyAverage.endMonth,
            monthlyGrowth: dwd.drivers.operationalMonthlyAverage.monthlyGrowth,
            yearlyGrowth: dwd.drivers.operationalMonthlyAverage.yearlyGrowth,
            customIncrease: dwd.drivers.operationalMonthlyAverage.customIncrease ?? false,
            customIncreases: valuesOrZeros(assumptions?.customIncreases)
        };
    }

    return {
        propertyIds: propertyIds,
        accountIds: accountIds,
        versionType: versionType,
        overwriteOnDuplicate: false,
        year: year,
        ...partialVars
    };
}

export interface IBulkAddDriversProps {
    property: Property;
    versionType: VersionType.Budget | VersionType.Reforecast;
    accounts: SelectableFinancialEntity[];
    cancel?: () => void;
}

export function BulkAddDrivers(props: IBulkAddDriversProps): ReactElement {
    const {properties} = useProperties();
    const authContext = useContext(AuthContext);
    const months = useMemo(() => getMonths(props.property), [properties]);

    const [selectedProperties, setSelectedProperties] = useState<Property[]>([props.property]);

    const yearAndMonth = useMemo(() => {
        let properties = selectedProperties;
        if(properties.length === 0) {
            properties = [props.property];
        }

        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        let year = properties[0]!.reforecastYear;
        let month = properties.map(p => p.reforecastStartMonthIndex).min();
        const minReforecastMonth = month;
        if(props.versionType === VersionType.Budget) {
            year += 1;
            month = 0;
        }

        return {
            reforecastYear: props.property.reforecastYear,
            minReforecastStartMonth: minReforecastMonth,
            budgetYear: props.property.reforecastYear + 1,
            year: year,
            month: month
        };
    }, [selectedProperties, props.versionType]);

    const {updateFormulaBarData} = useFormulaBarAccountTableMapper({
        propertyId: props.property.id,
        year: yearAndMonth.year,
        versionType: props.versionType
    });

    const [dummyDWD, setDummyDWD] = useState<IDriversAndWorksheetData>(
        buildDummyData(props.property.id, yearAndMonth.year, props.versionType, FORMULA_BAR_DUMMY_ACCOUNT_ID)
    );
    const driverAssumptions = useRef<Map<string, IDriverRow>>(new Map());
    const payrollCompItemMappings = useRef<Map<string, string>>(new Map());

    const [bulkAddDrivers] = useBulkAddDriversMutation();
    const [saving, setSaving] = useState<boolean>(false);
    const [opMonthlyAvgError, setOpMonthlyAvgError] = useState<string | null>(null);

    useEffect(() => {
        if(selectedProperties.length === 0) {
            return;
        }

        const opMonthlyAvg = dummyDWD.parsedData?.drivers.operationalMonthlyAverage;
        if(!opMonthlyAvg) {
            return;
        }

        const reforecastYear = selectedProperties[0]!.reforecastYear;
        const parts = opMonthlyAvg.endMonth.split("-");
        let endMonth = 11;
        let year = reforecastYear;
        if(parts.length >= 2 && parts[1] && parts[0]) {
            try {
                year = parseInt(parts[0], 10);
                endMonth = parseInt(parts[1], 10) - 1;
            } catch(e) {
                /* Ignore */
            }
        }

        if(year < reforecastYear) {
            setOpMonthlyAvgError(null);
            return;
        }

        const badProperties = selectedProperties.filter(p => p.reforecastStartMonthIndex <= endMonth);
        if(badProperties.length > 0) {
            setOpMonthlyAvgError("Operational Monthly Average end date exceeds maximum allowed for: " + badProperties.map(p => `${p.name} (${getMonths(p)[p.reforecastStartMonthIndex - 1]})`).join(", "));
        } else {
            setOpMonthlyAvgError(null);
        }
    }, [dummyDWD, selectedProperties]);

    function handleSave(dwd: IDriversAndWorksheetData, assumptions: Map<string, IDriverRow>): void {
        if(!dwd.parsedData) {
            return;
        }

        const vars =  mapForSave(
            selectedProperties.map(p => p.id),
            props.accounts.map(a => a.id),
            props.versionType,
            yearAndMonth.year,
            dwd.parsedData,
            assumptions,
            payrollCompItemMappings.current
        );

        setSaving(true);
        bulkAddDrivers({
            variables: vars
        }).then(res => {
            if(res.data?.bulkAddDrivers) {
                toast.success("Bulk added drivers successfully.");
            } else {
                toast.error("Bulk Add Failed!");
            }
        }).finally(() => {
            setSaving(false);
        });
    }

    function handleFormulaBarCommit(data: ISaveData): void {
        const newData = updateFormulaBarData(dummyDWD, data);
        data.pendingUpdates.add.payroll.forEach(p => {
            if(p.compId) {
                payrollCompItemMappings.current.set(p.itemType, p.compId);
            }
        });
        setDummyDWD(newData);
    }

    function handleTableUpdate(drivers: Map<string, IDriverRow>): void {
        driverAssumptions.current = drivers;
    }

    function hasDrivers(dummyDWD: IDriversAndWorksheetData): boolean {
        if(dummyDWD.parsedData) {
            const parsedData = dummyDWD.parsedData;
            const values = Object.values(parsedData.drivers);
            for(const v of values) {
                if(Array.isArray(v) && v.length > 0) {
                    return true;
                }
            }
            return !!dummyDWD.parsedData.drivers.operationalMonthlyAverage;
        }
        return false;
    }

    return <div style={{minHeight: "716px"}}>
        <div className={bulkCss.propertySelector}>
            <Label style={{marginBottom: ".5rem"}}>
                <span>Properties</span>
            </Label>
            <CustomZDDropdown
                    initialSelectedItems={selectedProperties.map(p => p.id)}
                    applySelectedItems={function (items: string[]): void {
                        if(properties) {
                            const activeProperties = properties.filter(x => items.includes(x.id));
                            setSelectedProperties(activeProperties);
                        }
                    }}
                    openDropdownPlaceholder={"Properties"}
                    closedDropdownPlaceholder={"Properties"}
                    options={properties?.map(p => ({value: p.id, label: p.name})) ?? []}
                    isError={selectedProperties.length === 0}
                    isMulti
                    allOption
                    errorMessage="At least one property is required."
            />
            <div className={bulkCss.errors}>{opMonthlyAvgError}</div>
        </div>
        <div className={css.accountFormulaBar}>
            <YearMonthRangeForProperty property={props.property} versionType={props.versionType} className={css.formulaDateRange} />
            <FormulaBar
                    year={yearAndMonth.reforecastYear}
                    budgetYear={yearAndMonth.budgetYear}
                    versionType={props.versionType}
                    currentPropertyId={props.property.id}
                    minReforecastMonth={yearAndMonth.minReforecastStartMonth}
                    accountId={FORMULA_BAR_DUMMY_ACCOUNT_ID}
                    readOnly={false}
                    driversAndWorksheetData={{
                        ...dummyDWD,
                        saveData: handleFormulaBarCommit
                    }}
                    disabledMenuItems={["revenue"]}
                    disableCycleDetection={true}
                    editableFxBarChecker={() => true}
                    isPropertyDriversUI
            />
        </div>
        {hasDrivers(dummyDWD) ?
            <NonSavingAccountTable
                year={yearAndMonth.year}
                months={months}
                versionType={props.versionType}
                reforecastStartMonth={yearAndMonth.month}
                combinedData={buildCombinedData(dummyDWD, driverAssumptions.current, authContext.operationalMetricTypes)}
                editEnd={handleTableUpdate}
                user={authContext.user}
            /> :
            <div className={bulkCss.selectDrivers}>Add Drivers to Begin!</div>
        }
        <div className={bulkCss.buttons}>
            {props.cancel && <Button className={bulkCss.button} isBasic onClick={props.cancel}>Cancel</Button>}
            <Button
                    className={bulkCss.button}
                    isPrimary
                    disabled={!hasDrivers(dummyDWD) || !!opMonthlyAvgError || saving}
                    onClick={() => handleSave(dummyDWD, driverAssumptions.current)}
            >Save</Button>
        </div>
    </div>;
}